import { getCleanAccessToken } from "@/utils/auth-token";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";
import { jwtDecode } from "jwt-decode";

type SessionTokenClaims = {
  actorUserId?: number | null;
};

/**
 * Whether a Lime admin is currently infiltrating a client account.
 *
 * The session JWT carries `actorUserId` — the admin behind the session — only
 * for impersonated sessions, which is the signal the server itself treats as
 * authoritative (`assertNotImpersonatingForMfaSetup` returns early when it is
 * null). Reading the token rather than `accountInfo.superAdmin` keeps this
 * usable outside React and correct before the account request has resolved.
 *
 * Fails open: an absent or unreadable token reports a genuine session, since
 * the alternative is silently changing behaviour for every real user whenever
 * the token shape shifts.
 */
export function isImpersonatedSession(): boolean {
  const token = getCleanAccessToken();
  if (!token) return false;

  try {
    return jwtDecode<SessionTokenClaims>(token).actorUserId != null;
  } catch (error) {
    sentryLog.warn(
      "Failed to decode session token for impersonation check",
      describeErrorForLog(error),
    );
    return false;
  }
}
